import { FieldForm } from "@/features/question/components/organisms/FieldForm"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs"
import { crmSystem } from "@/constants/crm/_shared/systems"
import { crmRegistry } from "@/features/crm_system/lib/_core/crmRegistry"
import { AllCrmsFieldsTypes } from "@/features/crm_system/lib/_core/crmSystem"
import { matchConfigurationData, matchConfigurationDataWithDefault, matchDefaultConfigurationData } from "@/lib/utils/crm/matchConfigurationData"
import { zodResolver } from "@hookform/resolvers/zod"
import { Plus } from "lucide-react"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react"
import { useFieldArray, useForm } from "react-hook-form"
import { questionFormDefaultValues, QuestionFormProps, QuestionFormRef, questionFormSchema, QuestionFormSchemaType } from "."
import { ConditionGroup } from "../../molecules/ConditionGroup"
import { merge } from "lodash"
import { DEFAULT_CRM_SYSTEM } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.constants.gen"
import { QuestionConfigurationParameterInput } from "../../../../parameters/components/atoms/QuestionConfigurationParameterInput"
import { BorderBox } from "@/components/atoms/BorderBox"
import { ParameterInputEvent } from "@/features/crm_system/lib/crm/_common/ConfigurationParameterInput"
import { FieldOptions } from "@/types/crm/tables/question"
import { fieldOptionsValueLabelSchema } from "@/lib/schema/question"
import z from "zod"



/**
 * To use this component, you need to pass ref to it from the parent component:
 * <AddQuestionForm ref={formRef} ... />.
 * Use the ref to access the form methods. For example:
 * const formRef = useRef<AddQuestionFormRef>(null);
 * const form = addQuestionFormRef.current?.form;.
 * Then you can access methods like this: form?.handleSubmit
 */
export const QuestionForm = forwardRef<QuestionFormRef, QuestionFormProps>(({
    defaultValues = questionFormDefaultValues,
    crm,
    crmProject,
    crmConfig,
    crmSystem,
    isFormDisabled = false,
    questions,
}, ref) => {
    const form = useForm<QuestionFormSchemaType>({
        resolver: zodResolver(questionFormSchema)
    })
    const defaultValuesExtended = useMemo(() => (merge({}, questionFormDefaultValues, defaultValues, { crm_system: crmSystem || DEFAULT_CRM_SYSTEM })), [crmSystem, defaultValues])
    const watchConditionalsEnabled = form.watch("selected_configuration.conditionals.enabled");
    const [isFieldTypeSetAutomatically, setIsFieldTypeSetAutomatically] = useState(false);
    const { fields, append: appendConditionGroup, remove: removeConditionGroup } = useFieldArray({
        control: form.control,
        name: "selected_configuration.conditionals.where.or",
    });
    const matchedConfigurationWithDefault = useMemo(() => matchConfigurationDataWithDefault(crm, crmProject, crmConfig), [crm, crmProject, crmConfig]);
    const questionMatchedConfiguration = useMemo(
        () => matchConfigurationData(crm, crm?.configuration), [crm]
    )?.parameter?.question
    const questionDefaultMatchedConfiguration = useMemo(
        () => matchDefaultConfigurationData(crm), [crm]
    )?.parameter?.question
    // TODO PROVIDE FULL CONFIGURATION INSTEAD OF QUESTION CONFIGURATION
    const questionMatchedConfigurationWithDefault = useMemo(
        () => matchedConfigurationWithDefault?.parameter?.question, [matchedConfigurationWithDefault]
    );

    const resetForm = useCallback(() => {
        form.reset({
            ...defaultValuesExtended,
        })
    }, [defaultValuesExtended, form])

    useEffect(() => {
        resetForm()
    }, [defaultValuesExtended, resetForm])


    useImperativeHandle(ref, () => ({
        form,
        resetForm,
    }), [form, resetForm])
    crmSystem = crmSystem || crm?.crm_system || defaultValuesExtended.crm_system;
    const areQuestionsForConditionalsAvailable = (!questions || (questions?.length === 0));
        const crmSystemConfig = crmSystem ? crmRegistry.getSystemById(crmSystem) : null;
    const handleEvent = useCallback((event: ParameterInputEvent) => {
        if (event.type === 'fieldTypeReset') {
            setIsFieldTypeSetAutomatically(false);
            crmRegistry.resetFieldForm(crmSystem, form.getValues('field_type'), form);
            return;
        }
        if (event.type === 'fieldTypeResolved' && event.question?.field_type) {
            if(crmSystemConfig?.notSupportedFieldTypes?.includes(event.question?.field_type)){
                form.setError(`field_type`, { type: 'manual', message: `"${event.question?.field_type}" field type is not supported for ${crmSystemConfig.name}. Export for this column is not available.` });
                return;
            }
            console.log('event')
            console.log(event)
            console.log(event.question?.field_options)
            console.log(normalizeOptions(event.question?.field_options || []))

            form.clearErrors(`field_type`);
            form.setValue('field_type', event.question?.field_type);
            form.setValue('field_date_options', event.question?.field_date_options ?? null)
            // TODO Field options are not being displayed in form
            form.setValue('field_options', normalizeOptions(event.question?.field_options || []) ?? [])
            form.setValue('field_decimal_places', event.question?.field_decimal_places ?? null)
            form.setValue('field_default_value', event.question?.field_default_value ?? null)
            form.setValue('field_integer_places', event.question?.field_integer_places ?? null)
            form.setValue('field_label', event.question?.field_label ?? null)
            form.setValue('field_max', event.question?.field_max ?? null)
            form.setValue('field_max_label', event.question?.field_max_label ?? null)
            form.setValue('field_min', event.question?.field_min ?? null)
            form.setValue('field_min_label', event.question?.field_min_label ?? null)
            form.setValue('field_placeholder', event.question?.field_placeholder ?? null)
            form.setValue('field_step', event.question?.field_step ?? null)
            if(event.question?.field_type){
                setIsFieldTypeSetAutomatically(true);
            }else{
                setIsFieldTypeSetAutomatically(false);
            }
        }
    }, [form, crmSystem, crmSystemConfig]);
    return (
        <Form {...form}>
            <form>
                <Tabs defaultValue="questionParameters">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="questionParameters">Question Parameters</TabsTrigger>
                        <TabsTrigger value="conditionals" disabled={areQuestionsForConditionalsAvailable}>
                            Conditionals
                            {watchConditionalsEnabled && (
                                <span className="flex items-center justify-center px-2 py-px text-xs font-medium text-white bg-blue-400 rounded-md ms-1">
                                    {fields.length}
                                </span>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="questionParameters">
                        <div className="grid grid-cols-2 gap-2 pb-1">
                            <div>
                                <FormField
                                    name="question"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Question</FormLabel>
                                            <FormControl>
                                                <Input {...field} autoComplete="off" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    name="description"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Description</FormLabel>
                                            <FormControl>
                                                <Input {...field} autoComplete="off" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />



                                {questionMatchedConfigurationWithDefault && questionMatchedConfiguration && (
                                    <>
                                        <h3 className="pt-2 my-3 font-semibold border-t">Configuration</h3>
                                        <QuestionConfigurationParameterInput
                                            crmSystem={crmSystem}
                                            parameters={[...(questionDefaultMatchedConfiguration || []), ...(questionMatchedConfiguration || [])]}
                                            form={form}
                                            fullConfiguration={matchedConfigurationWithDefault}
                                            handleEvent={handleEvent}
                                        />
                                    </>
                                )}


                            </div>
                            <div>
                                <FormField
                                    name="field_type"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Field Type</FormLabel>
                                            <FormControl>
                                                <Select onValueChange={field.onChange} value={field.value} disabled={isFormDisabled || isFieldTypeSetAutomatically}>
                                                    <SelectTrigger className="" >
                                                        <SelectValue placeholder="Select Question Type" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {crmRegistry.getFieldsList(crmSystem).map((option) => (
                                                            <SelectItem key={option.id} value={option.id}>
                                                                {option.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormMessage />
                                            {isFieldTypeSetAutomatically ? (
                                                <BorderBox variant='info' size='sm'>
                                                    Field type was set automatically based on the selected configuration.
                                                </BorderBox>
                                            ) : null}
                                        </FormItem>
                                    )}
                                />
                                <FieldForm
                                    form={form}
                                    fieldType={form?.watch('field_type') as AllCrmsFieldsTypes}
                                    isFormDisabled={isFormDisabled}
                                    crmSystem={crmSystem}
                                    isFieldTypeSetAutomatically={isFieldTypeSetAutomatically}
                                />
                            </div>
                        </div>
                    </TabsContent>
                    <TabsContent value="conditionals">
                        {(questions) && (
                            <div className="flex flex-col gap-3 pt-2 mt-3 border-t">
                                <FormField
                                    name={"selected_configuration.conditionals.enabled"}
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem className="flex items-center gap-2 space-y-0">
                                            <Checkbox
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                            <FormLabel>Show question only when conditionals are met</FormLabel>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                {watchConditionalsEnabled && (
                                    <>
                                        <div className="flex flex-col gap-2">
                                            {fields.map((field, index, arr) => {
                                                return (
                                                    <ConditionGroup
                                                        key={field.id}
                                                        index={index}
                                                        form={form}
                                                        removeConditionGroup={removeConditionGroup}
                                                        questions={questions}
                                                        crmSystem={crmSystem as crmSystem || DEFAULT_CRM_SYSTEM}
                                                        length={arr.length}
                                                    />
                                                )
                                            })}
                                        </div>
                                        <div>
                                            <Button size="sm" variant="outline" type="button" className="mt-2" onClick={() => appendConditionGroup({})}>
                                                <Plus size="16" /> {fields.length > 0 ? 'OR' : 'Add condition group'}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        {areQuestionsForConditionalsAvailable && (
                            <p className='py-4 text-sm text-center'>Questions not found</p>
                        )}
                    </TabsContent>
                </Tabs>
            </form>
        </Form>
    )
})

const normalizeOptions = (options: FieldOptions): string[] => {
    if(!Array.isArray(options)){
        options = options.options
    }
    if(typeof options[0] === 'string') return options as string[]
    return (options as Array<z.infer<typeof fieldOptionsValueLabelSchema>>).map(o=>o.label)
}